import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Table,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  Input,
  Spinner,
  Badge,
} from "reactstrap";
import { toast } from "react-toastify";
import { baseUrl } from "../../Network";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { handleAuthError } from "../../utils";
import { isMarketerReadOnly } from "../../helpers/adminRole";
import withAuth from "../withAuth";

const formatDate = (value) => {
  if (!value) return "-";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
};

const parentLabel = (parent) => {
  if (!parent) return "-";
  const name = `${parent.firstName || ""} ${parent.lastName || ""}`.trim();
  return name || parent.email || `Parent #${parent.id}`;
};

const ManageAiFeedback = () => {
  document.title = "Manage Feedback | RYD Admin";
  const readOnly = isMarketerReadOnly();
  const [loading, setLoading] = useState(true);
  const [feedbacks, setFeedbacks] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [archivingId, setArchivingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [feedbackToDelete, setFeedbackToDelete] = useState(null);
  const [viewModal, setViewModal] = useState(false);
  const [feedbackToView, setFeedbackToView] = useState(null);

  const fetchFeedbacks = async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter && statusFilter !== "all") params.status = statusFilter;
      if (search.trim()) params.search = search.trim();
      const response = await axios.get(`${baseUrl}/admin/feedback/all`, { params });
      setFeedbacks(Array.isArray(response?.data?.data) ? response.data.data : []);
    } catch (error) {
      handleAuthError(error);
      toast.error("Failed to load feedback");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFeedbacks();
  }, [statusFilter]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchFeedbacks();
  };

  const openViewModal = (item) => {
    setFeedbackToView(item);
    setViewModal(true);
  };

  const closeViewModal = () => {
    setViewModal(false);
    setFeedbackToView(null);
  };

  const openDeleteModal = (item) => {
    setFeedbackToDelete(item);
    setDeleteModal(true);
  };

  const closeDeleteModal = () => {
    if (deletingId) return;
    setDeleteModal(false);
    setFeedbackToDelete(null);
  };

  const handleArchiveToggle = async (item) => {
    try {
      setArchivingId(item.id);
      const nextStatus = !item.status;
      await axios.put(`${baseUrl}/admin/feedback/archive/${item.id}`, {
        status: nextStatus,
      });
      setFeedbacks((prev) =>
        prev.map((f) => (f.id === item.id ? { ...f, status: nextStatus } : f))
      );
      toast.success(nextStatus ? "Feedback restored" : "Feedback archived");
    } catch (error) {
      handleAuthError(error);
      toast.error("Failed to update feedback status");
    } finally {
      setArchivingId(null);
    }
  };

  const handleDeleteFeedback = async () => {
    if (!feedbackToDelete) return;
    try {
      setDeletingId(feedbackToDelete.id);
      await axios.delete(`${baseUrl}/admin/feedback/${feedbackToDelete.id}`);
      setFeedbacks((prev) => prev.filter((f) => f.id !== feedbackToDelete.id));
      toast.success("Feedback deleted successfully");
      setDeleteModal(false);
      setFeedbackToDelete(null);
    } catch (error) {
      handleAuthError(error);
      toast.error("Failed to delete feedback");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="AI Tools" breadcrumbItem="Manage Feedback" />
        <Row className="mb-3">
          <Col md={4}>
            <Input
              type="select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All feedback</option>
              <option value="active">Active</option>
              <option value="archived">Archived</option>
            </Input>
          </Col>
          <Col md={8}>
            <form className="d-flex gap-2" onSubmit={handleSearch}>
              <Input
                type="search"
                placeholder="Search parent, email, curriculum, or comment…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <Button type="submit" color="primary" outline>
                Search
              </Button>
            </form>
          </Col>
        </Row>
        <Row>
          <Col lg={12}>
            <Card>
              <CardBody>
                {loading ? (
                  <div className="text-center py-5">Loading...</div>
                ) : (
                  <div className="table-responsive">
                    <Table className="align-middle mb-0">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Parent</th>
                          <th>Curriculum</th>
                          <th>Rating</th>
                          <th>Comment</th>
                          <th>Date</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {feedbacks.map((item, index) => (
                          <tr key={item.id}>
                            <td>{index + 1}</td>
                            <td>
                              <div>{parentLabel(item.parent)}</div>
                              <small className="text-muted">{item.parent?.email || ""}</small>
                            </td>
                            <td>
                              <div>{item.curriculum?.title || "-"}</div>
                              {item.curriculum?.slug ? (
                                <small className="text-muted">
                                  <code>{item.curriculum.slug}</code>
                                </small>
                              ) : null}
                            </td>
                            <td>{item.rating != null ? `${item.rating}/5` : "-"}</td>
                            <td style={{ maxWidth: 240 }}>
                              <span className="d-inline-block text-truncate" style={{ maxWidth: 220 }}>
                                {item.comment || "-"}
                              </span>
                            </td>
                            <td>{formatDate(item.createdAt)}</td>
                            <td>
                              <Badge color={item.status ? "success" : "secondary"}>
                                {item.status ? "Active" : "Archived"}
                              </Badge>
                            </td>
                            <td>
                              <div className="d-flex flex-wrap gap-2">
                                <Button
                                  type="button"
                                  color="primary"
                                  outline
                                  size="sm"
                                  onClick={() => openViewModal(item)}
                                >
                                  View
                                </Button>
                                {!readOnly && (
                                  <>
                                    <Button
                                      type="button"
                                      color={item.status ? "warning" : "success"}
                                      outline
                                      size="sm"
                                      disabled={archivingId === item.id}
                                      onClick={() => handleArchiveToggle(item)}
                                    >
                                      {archivingId === item.id ? (
                                        <>
                                          <span
                                            className="spinner-border spinner-border-sm me-1"
                                            role="status"
                                            aria-hidden="true"
                                          />
                                          …
                                        </>
                                      ) : item.status ? (
                                        "Archive"
                                      ) : (
                                        "Restore"
                                      )}
                                    </Button>
                                    <Button
                                      type="button"
                                      color="danger"
                                      outline
                                      size="sm"
                                      disabled={deletingId === item.id}
                                      onClick={() => openDeleteModal(item)}
                                    >
                                      Delete
                                    </Button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                        {!feedbacks.length && (
                          <tr>
                            <td colSpan={8} className="text-center py-4">
                              No feedback submitted yet
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <Modal isOpen={viewModal} toggle={closeViewModal} size="lg">
        <ModalHeader toggle={closeViewModal}>Feedback details</ModalHeader>
        <ModalBody>
          {feedbackToView && (
            <>
              <p className="mb-2">
                <strong>Parent:</strong> {parentLabel(feedbackToView.parent)}
                {feedbackToView.parent?.email ? ` (${feedbackToView.parent.email})` : ""}
              </p>
              <p className="mb-2">
                <strong>Curriculum:</strong>{" "}
                {feedbackToView.curriculum?.title || "-"}
                {feedbackToView.curriculum?.slug ? (
                  <>
                    {" "}
                    (<code>{feedbackToView.curriculum.slug}</code>
                  </>
                ) : null}
              </p>
              <p className="mb-2">
                <strong>Rating:</strong>{" "}
                {feedbackToView.rating != null ? `${feedbackToView.rating}/5` : "Not provided"}
              </p>
              <p className="mb-2">
                <strong>Date:</strong> {formatDate(feedbackToView.createdAt)}
              </p>
              <p className="mb-2">
                <strong>Status:</strong>{" "}
                {feedbackToView.status ? "Active" : "Archived"}
              </p>
              <p className="mb-1">
                <strong>Comment:</strong>
              </p>
              <p className="mb-0" style={{ whiteSpace: "pre-wrap" }}>
                {feedbackToView.comment}
              </p>
            </>
          )}
          <div className="d-flex justify-content-end mt-3">
            <Button color="secondary" onClick={closeViewModal}>
              Close
            </Button>
          </div>
        </ModalBody>
      </Modal>

      <Modal isOpen={deleteModal} toggle={closeDeleteModal}>
        <ModalHeader toggle={closeDeleteModal}>Confirm delete</ModalHeader>
        <ModalBody>
          <p>
            Are you sure you want to delete feedback from{" "}
            <strong>{parentLabel(feedbackToDelete?.parent)}</strong>?
          </p>
          <p className="text-muted mb-0">This action cannot be undone.</p>
          <div className="d-flex justify-content-end mt-3">
            <Button color="secondary" onClick={closeDeleteModal} className="me-2" disabled={!!deletingId}>
              Cancel
            </Button>
            <Button color="danger" onClick={handleDeleteFeedback} disabled={!!deletingId}>
              {deletingId ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Deleting…
                </>
              ) : (
                "Delete feedback"
              )}
            </Button>
          </div>
        </ModalBody>
      </Modal>
    </div>
  );
};

export default withAuth(ManageAiFeedback);
